import { isEqual } from 'lodash'
import moment from 'moment'
import ApiError from '../errors/ApiError.js'

export default {
  mounted () {
    // We must validate
    if (this.invoice) {
      this.validateInvoice(this.invoice)
    }
  },
  watch: {
    invoice: function (invoice, prevInvoice) {
      const isLoaded = !this.invoiceIsLoading
      if (!isEqual(prevInvoice, invoice)) {
        // Invoice changed, validate it
        if (isLoaded) {
          this.validateInvoice(invoice)
        }
      }
    }
  },
  computed: {
    /**
     * Returns shipping order for current invoice.
     * @public
     */
    currentShippingOrder: function () {
      return Object.values(this.shippingOrders).reduce(
        (order, currentIteration) => {
          if (currentIteration.invoice_id === Number(this.currentInvoiceId)) {
            return currentIteration
          } else {
            return order
          }
        },
        null
      )
    },
    /**
     * Returns all shipping orders for customer.
     * @public
     */
    shippingOrders: function () {
      return this.$store.getters.shippingOrders
    },
    /**
     * Returns the tax destination of invoice.
     * @see [Checkout: Sales Tax](docs/CHECKOUT.md#SalesTax)
     * @public
     */
    taxDestination: function () {
      const DEFAULT_DESTINATION = 'NC'
      if (this.invoice && this.currentShippingOrder) {
        // If pickup order, destination is NC
        if (this.selectedPickUp === true && (this.currentShippingOrder.status_id === 5 || this.currentShippingOrder.status_id === 10)) {
          return DEFAULT_DESTINATION
        } else if (parseInt(this.invoice.invoice_tax_override) !== 0) {
          // If invoice has tax override, destination is
          // either NC (like a pickup) or customer billing address

          // If 1, NC
          if (parseInt(this.invoice.invoice_tax_override) === 1) {
            return DEFAULT_DESTINATION
          } else if (parseInt(this.invoice.invoice_tax_override) === -1) {
            // TODO: validate customer billing address, for now return nothing so they can pick
            return ''
          }
        } else {
          // Otherwise, tax destination is the state on
          // current shipping order
          return this.currentShippingOrder.state
        }
      }
      return ''
    },
    /**
     *
     * @public
     */
    showLoadingOverlay: function () {
      if (this.taxReloadInProgress) {
        return true
      }
      if (this.updatingExemptionCertificate) {
        return true
      }
      if (typeof this.apiResponse.get_customer_invoice === 'undefined') {
        return true
      }
      return false
    },
    /**
     *
     * @public
     */
    selectedShippingOption: function () {
      if (
        this.invoice &&
        this.invoice.invoice_items &&
        this.invoice.invoice_items.length > 0
      ) {
        let selectedOption = null
        this.invoice.invoice_items.forEach(item => {
          if (item.shipping_charge === 'y') {
            selectedOption = item
          }
        })

        return selectedOption
      } else {
        return null
      }
    },
    /**
     *
     * @public
     */
    selectedPickUp: function () {
      if (
        this.invoice &&
        this.invoice.invoice_items &&
        this.invoice.invoice_items.length > 0
      ) {
        return this.invoice.shipping_order_pick_up
      }

      return false
    },
    /**
     *
     * @public
     */
    nonShippingInvoiceItems: function () {
      if (
        this.invoice &&
        this.invoice.invoice_items &&
        this.invoice.invoice_items.length > 0
      ) {
        let items = []
        this.invoice.invoice_items.forEach(item => {
          if (item.shipping_charge === 'n') {
            items.push(item)
          }
        })

        return items
      } else {
        return null
      }
    },
    /**
     *
     * @public
     */
    total: function () {
      return this.itemsTotal ? this.itemsTotal : 0
    },
    /**
     *
     * @public
     */
    currentInvoiceId: function () {
      return this.$store.getters.checkoutInvoiceId
    },
    /**
     *
     * @public
     */
    itemsTotal: function () {
      let total = 0

      const tempInvoice = this.invoice
      if (tempInvoice && tempInvoice.length !== 0) {
        return tempInvoice.invoice_balance
      }

      return total
    },
    /**
     *
     * @public
     */
    invoices: function () {
      return this.$store.getters.invoices
    },
    /**
     * The current invoice.
     *
     * @public
     */
    invoice: function () {
      const key = `INVOICEID_${this.currentInvoiceId}`
      const tempInvoice = this.invoices[key]
      // If tempInvoice is undefined and not still loading from API we return false to trigger the invoice watcher
      return typeof tempInvoice !== 'undefined' ? tempInvoice : false
    },
    /**
     * @public
     */
    invoiceIsLoading: function () {
      return this.$store.state.invoiceIsLoading
    },
    /**
     * Assert if staff is viewing invoice in staff view
     *
     * @public
     */
    isStaffView: function () {
      // If invoice customer id is not equal to logged in customer and logged in customer is staff
      // (api will not return invoice unless authenticated customer id is same as invoice id OR authenticated customer is staff)
      if (
        this.invoice &&
        this.invoice.customer_id &&
        this.$store.getters.customer.customer_id !== this.invoice.customer_id &&
        typeof this.apiResponse.get_customer_invoice !== 'undefined' &&
        typeof this.apiResponse.get_account !== 'undefined'
      ) {
        return true
      }
      return false
    },
    /**
     * The customer name for the invoice
     *
     * @public
     */
    customerName: function () {
      if(!this.invoice){
        return ""
      }
      return this.invoice.customer_display_name
      // return this.$store.getters.customer.display_name
    },
    /**
     * The API response object
     *
     * @public
     */
    apiResponse: function () {
      return this.$store.getters.apiResponse
    },
    /**
     * The list of invoice payments
     *
     * @public
     */
    invoicePayments: function () {
      const payments = this.invoice ? this.invoice.invoice_payments : []
      let paymentList = []

      if (payments) {
        Object.keys(payments).forEach(function (key) {
          var d = new Date(0) // The 0 there is the key, which sets the date to the epoch
          d.setUTCSeconds(payments[key].creation)

          paymentList.push({
            amount: payments[key].display_amount,
            date: d.toLocaleDateString(),
            invoice_payment_id: payments[key].invoice_payment_id,
            payment_type: payments[key].payment_type,
            cc_expire_month: payments[key].cc_expire_month,
            cc_expire_year: payments[key].cc_expire_year,
            cc_last_four: payments[key].cc_last_four,
            cc_type: payments[key].cc_type
          })
        })
      }

      return paymentList
    },
    /**
     * The breadcrumb objects for page navigation
     *
     * @public
     */
    breadCrumbs: function () {
      return [
        {
          active: false,
          canNavigate: true,
          title: 'Auction Items',
          route: 'auctionItems'
        },
        {
          active: false,
          canNavigate: this.useArta && this.canGoToShipping,
          hidden: !this.useArta,
          title: 'Shipping',
          route: 'shipping'
        },
        {
          active: false,
          canNavigate: true,
          hidden: !this.useArta,
          title: 'Options',
          route: 'shippingOptions'
        },
        {
          active: true,
          canNavigate: false,
          title: 'Payment Info',
          route: 'paymentInfo'
        }
      ]
    },
    /**
     * The list of inventory ids on the invoice
     *
     * @public
     */
    inventory_ids: function () {
      let tempInvoice = this.invoice
      let out = []
      if (tempInvoice && typeof tempInvoice.invoice_items === 'undefined') {
        return out
      }
      if (tempInvoice && tempInvoice.invoice_items) {
        for (let i = 0; i < tempInvoice.invoice_items.length; i++) {
          let item = tempInvoice.invoice_items[i]
          if (item.inventory_id > 0) {
            out.push(item.inventory_id)
          }
        }
      }
      return out
    },
    /**
     * Assert if API call in progress
     *
     * @public
     */
    isThinking: function () {
      if (this.error.show) {
        return true
      }
      if (this.braintreeIsThinking) {
        return true
      }
      let apiPending = this.$store.getters.apiPending
      if (typeof apiPending.pay_and_ship !== 'undefined') {
        return true
      }
      return false
    },
    /**
     * The list of shipping quotes
     *
     * @public
     */
    quoteCandidates: function () {
      return this.$store.getters.quoteCandidates
    },
    /**
     * If we can navigate to Shipping tab.
     *
     * @public
     */
    canGoToShipping: function () {
      let quoteCandidates = this.quoteCandidates
      // Must have items in building
      const needsCollection =
        this.invoice && this.invoice.invoice_items
          ? this.invoice.invoice_items.reduce((collector, current) => {
            if (
              current.shipping_charge === 'n' &&
              current.has_left_building !== 'y'
            ) {
              collector.push(current)
            }
            return collector
          }, [])
          : []
      if (this.inventory_ids.length === 0 || needsCollection.length === 0) {
        return false
      }
      if (!quoteCandidates.length) {
        return true
      }
      if (quoteCandidates[0].isCustom) {
        return false
      }
      return true
    }
  },
  methods: {
    /**
     *
     * @public
     */
    goToRoute(route, canNavigate) {
      if (canNavigate) {
        this.$router.push({ name: route })
      }
    },
    /**
     *
     * @public
     */
    openError(title, message, details) {
      this.showPaymentProcessing = false
      this.paymentInProgress = false
      this.error.title = ''
      this.error.message = ''
      if (typeof title === typeof 'asdf') {
        this.error.title = title
      }
      if (typeof message === typeof 'asdf') {
        this.error.message = message
      }
      this.error.show = true
    },
    /**
     *
     * @public
     */
    openDefaultError() {
      this.openError(
        'Something went wrong...',
        'Please try again later. If you see this more than once, please contact Leland Little for assistance.'
      )
    },
    /**
     *
     * @public
     */
    handleDisabledCardClick() {
      if (this.creditCardPaymentRestricted) {
        this.alertTitle =
          'This invoice contains items that are restricted by the credit card processor and, therefore, can not be paid for by credit card.'
        this.openError('', this.alertTitle)
      }
    },
    /**
     *
     * @public
     */
    openErrorPaymentFailed(reason) {
      if (typeof reason !== typeof 'asdf' || reason.length < 1) {
        reason =
          'There was a problem processing your payment. Please try again or contact Leland Little for assistance.'
      }
      this.openError('Payment Not Accepted', reason)
    },
    /**
     *
     * @public
     */
    closeError() {
      this.error.show = false
      if (this.error.fatal) {
        this.exit()
      }
    },
    /**
     *
     * @public
     */
    exitToInvoices() {
      window.location.replace(this.getUrlHost(this.$store.getters.apiPath))
    },
    /**
     *
     * @public
     */
    exit() {
      // apiMixin
      window.location.replace(this.getUrlHost(this.$store.getters.apiPath))
    },
    /**
     *
     * @public
     */
    toggleUploadCertificateForm() {
      this.uploadingCertificate = !this.uploadingCertificate
      if (this.uploadingCertificate === false) {
        // They closed CertCapture modal. Did they upload a certificate?
        if (this.certificateUploadSuccess) {
          this.reloadTaxDataLoop()
        }
      }
    },
    /**
     *
     * @public
     */
    showUploadCertificateDialog() {
      this.showCertUploadRedirectConfirmation = !this
        .showCertUploadRedirectConfirmation
    },
    /**
     *
     * @public
     */
    getExemptionStatus() {
      this.loadingExemptionStatus = true
      var apiPayload = {}
      apiPayload.apiEndpoint = 'customer'
      apiPayload.postData = {}
      apiPayload.postData.action = 'get_exemption_status'
      apiPayload.postData.invoice_id = this.currentInvoiceId
      this.$store.dispatch('apiCall', apiPayload).then(async response => {
        const {certificates, tax_exempt, tax_message} = await response.data.payload.response;
        const exemptionStatus = {
          certificates: certificates || [],
          tax_exempt: tax_exempt || false,
          tax_message: tax_message || '',
        };
        this.exemptionStatus = exemptionStatus;
        this.loadingExemptionStatus = false
      })
    },
    /**
     *
     * @public
     */
    onCertSuccess(certificateId) {
      // The certificate was uploaded. This is our callback.
      // We will check this when they close the CertCapture modal.
      // If it is true: Recalculate taxes.
      this.certificateUploadSuccess = true
      // If we got a certificate ID, we can send it to the API with a new signed date equal to invoice.creation.
      // This will set the customer to tax exempt for this invoice.
      if (certificateId) {
        this.setInvoiceTaxExempt(certificateId)
      }
    },
    setInvoiceTaxExempt(id) {
      // The new certificate effective date.
      var self = this
      const signedDate = this.invoice ? moment.unix(this.invoice.invoice_creation) : null;
      if (signedDate && signedDate.isValid()) {
        this.updatingExemptionCertificate = true
        var apiPayload = {}
        apiPayload.apiEndpoint = 'customer'
        apiPayload.postData = {}
        apiPayload.postData.action = 'update_exemption_certificate'
        apiPayload.postData.certificate_id = id
        apiPayload.postData.attributes = {};
        apiPayload.postData.attributes.signedDate = signedDate.format('YYYY-MM-DD')
        this.$store.dispatch('apiCall', apiPayload).then(response => {
          self.updatingExemptionCertificate = false
          self.taxReloadInProgress = false
        })
      }
    },
    /**
     *
     * @public
     */
    redirectToCertCapture() {
      window.location.href =
        'https://app.certexpress.com/?c=3352537246434f3367344831426267653848493d'
    },
    /**
     *
     * @public
     */
		reloadTaxDataLoop () {
			// When CertCapture receives the certificate, they do not set the customer to tax exempt
			// immediately, there is usually a delay of x number of seconds. This loop will
			// request to recalculate the taxes after certificate upload until the tax drops to 0,
			// or until 5 attempts to recalculate have been made.
			if (this.uploadingCertificate) {
				this.uploadingCertificate = false
			}
			this.taxReloadInProgress = true
			const retryPromise = new Promise((resolve, reject) => {
				if (this.taxReloadTries > 0) {
					this.taxReloadTries = this.taxReloadTries - 1
					this.$store.dispatch('getCustomerInvoice', {
						invoiceId: this.currentInvoiceId,
						recalculateTax: true,
						taxExemptionUpload: true,
						callback: resolve
					})
				}
			})
			retryPromise
				.then(response => {
					let invoice = this.invoices['INVOICEID_' + this.currentInvoiceId]
					if (invoice && invoice.invoice_tax_total === 0) {
						this.taxReloadTries = 0
						this.taxReloadInProgress = false
						// If we have a temporary convenience fee, we need to remove it and reload page
						if (this.invoice.invoice_items.some(item => item.convenience_fee === 'y' && item.description === 'Convenience Fee') === true) {
							this.handleConvenienceFee()
							return
						}
						// Done reloading, refresh page.
						window.location.reload()
						return
					}
					if (this.taxReloadTries !== 0) {
						setTimeout(() => {
							this.reloadTaxDataLoop()
						}, 2000)
						return
					}
					// If we have a temporary convenience fee, we need to remove it and reload page
					if (this.invoice.invoice_items.some(item => item.convenience_fee === 'y' && item.description === 'Convenience Fee') === true) {
						this.handleConvenienceFee()
						return
					}
					// Done reloading, refresh page.
					window.location.reload()
				})
				.catch(e => {
					this.taxReloadTries = 0
					this.taxReloadInProgress = false
				})
		},
		/**
		 * Recalculate the temporary convenience fee
		 * @public
		 * @returns {void}
		 */
		handleConvenienceFee: function () {
			this.removeConvienceFee()
				.then(() => {
					// Instead of adding convenience fee again, we will just reload the page
					window.location.reload()
				})
				.catch(err => {
					const exception = new ApiError(err)
					console.error(exception.getMessage())
					this.isLoading = false
				})
		},
		/**
		 * Add a temporary convenience fee to the invoice
		 * @public
		 * @returns {Promise}
		 */
		addConvenienceFee: function () {
			this.isLoading = true
			var apiPayload = {}
			apiPayload.apiEndpoint = 'account'
			apiPayload.postData = {}
			apiPayload.postData.action = 'add_convenience_fee'
			apiPayload.postData.invoice_id = this.invoice.invoice_id
			return this.$store.dispatch('apiCall', apiPayload)
		},
		/**
		 * Remove the temporary convenience fee from the invoice
		 * @public
		 * @returns {Promise}
		 */
		removeConvienceFee: function () {
			this.isLoading = true
			var apiPayload = {}
			apiPayload.apiEndpoint = 'account'
			apiPayload.postData = {}
			apiPayload.postData.action = 'remove_convenience_fee'
			apiPayload.postData.invoice_id = this.invoice.invoice_id
			return this.$store.dispatch('apiCall', apiPayload)
		}
	}
}
